import { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { DateTime } from 'luxon';
import { Maximize2, QrCode, WifiOff } from 'lucide-react';
import { t } from '@/lib/i18n';

const KEY_STORE = 'wf.kiosk.key';

interface TokenResponse {
  token: string;
  expiresInMs: number;
  deviceName: string;
  companyName: string;
  serverTime: string;
  timezone: string;
}

function readKey(): string | null {
  // Pairing link: /kiosk#key=... (the fragment is never sent to the server or logged).
  const m = window.location.hash.match(/key=([A-Za-z0-9_-]+)/);
  if (m) {
    try {
      localStorage.setItem(KEY_STORE, m[1]!);
    } catch {
      /* storage blocked: keep the key only for this page load */
    }
    history.replaceState(null, '', window.location.pathname);
    return m[1]!;
  }
  try {
    return localStorage.getItem(KEY_STORE);
  } catch {
    return null;
  }
}

/**
 * Full-screen page for the tablet at the office door. It shows a QR code that changes
 * every 30 seconds; employees scan it with their phone to clock in or out.
 * No user signs in here — the tablet is paired with a device key from the admin screen.
 */
export function KioskPage() {
  const [key] = useState(readKey);
  const [data, setData] = useState<TokenResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(false);
  const [now, setNow] = useState(Date.now());
  const canvas = useRef<HTMLCanvasElement>(null);
  const clockOffset = useRef(0);

  useEffect(() => {
    // The kiosk has its own fixed dark look; the palette variables must be the light
    // (literal) ones so slate-950 really is near-black.
    const root = document.documentElement;
    const wasDark = root.classList.contains('dark');
    root.classList.remove('dark');
    return () => {
      if (wasDark) root.classList.add('dark');
    };
  }, []);

  useEffect(() => {
    if (!key) return;
    let timer: ReturnType<typeof setTimeout>;
    let stop = false;
    const load = async () => {
      try {
        const res = await fetch('/api/kiosk/token', { headers: { 'X-Device-Key': key, Accept: 'application/json' }, cache: 'no-store' });
        const body = await res.json().catch(() => null);
        if (!res.ok) {
          setError(body?.error?.message ?? t('Error {status}', { status: res.status }));
          setData(null);
          if (res.status !== 401) timer = setTimeout(load, 15_000);
          return;
        }
        const tok = body as TokenResponse;
        clockOffset.current = new Date(tok.serverTime).getTime() - Date.now();
        setData(tok);
        setError(null);
        setOffline(false);
        if (!stop) timer = setTimeout(load, Math.max(2_000, tok.expiresInMs + 250));
      } catch {
        setOffline(true);
        if (!stop) timer = setTimeout(load, 10_000);
      }
    };
    void load();
    return () => {
      stop = true;
      clearTimeout(timer);
    };
  }, [key]);

  useEffect(() => {
    if (!data || !canvas.current) return;
    const url = `${window.location.origin}/scan?t=${encodeURIComponent(data.token)}`;
    const size = Math.min(window.innerWidth, window.innerHeight) * 0.62;
    void QRCode.toCanvas(canvas.current, url, { width: Math.max(220, Math.round(size)), margin: 2, errorCorrectionLevel: 'M', color: { dark: '#0f172a', light: '#ffffff' } });
  }, [data]);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const local = data ? DateTime.fromMillis(now + clockOffset.current).setZone(data.timezone) : null;

  if (!key) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-slate-950 p-8 text-center text-slate-200">
        <QrCode className="h-14 w-14 text-slate-500" />
        <h1 className="text-xl font-semibold">{t('This tablet is not paired')}</h1>
        <p className="max-w-md text-sm text-slate-400">
          {(() => {
            // Keep the menu path bold inside a single translatable sentence.
            const [before, after] = t('An administrator can open {path}, create a “Door QR kiosk” and open its pairing link on this tablet.').split('{path}');
            return (
              <>
                {before}
                <b>{t('Attendance → Devices')}</b>
                {after}
              </>
            );
          })()}
        </p>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen select-none flex-col items-center justify-between bg-slate-950 p-6 text-slate-100">
      <header className="flex w-full items-center justify-between text-sm text-slate-400">
        <span className="rounded-lg bg-white px-3 py-1.5">
          <img src="/api/meta/logo?variant=mark" alt={data?.companyName ?? ''} className="h-7 w-auto" />
        </span>
        <button
          className="inline-flex items-center gap-1 rounded px-2 py-1 hover:bg-slate-800"
          onClick={() => document.documentElement.requestFullscreen?.().catch(() => undefined)}
          aria-label={t('Full screen')}
        >
          <Maximize2 className="h-4 w-4" />
        </button>
      </header>
      <main className="flex flex-col items-center gap-5">
        {local && (
          <div className="text-center">
            <p className="text-6xl font-semibold tabular-nums tracking-tight">{local.toFormat('HH:mm')}</p>
            <p className="mt-1 text-slate-400">{local.toFormat('cccc d LLLL')}</p>
          </div>
        )}
        {error ? (
          <p className="max-w-md rounded-lg bg-red-950 p-4 text-center text-red-200">{error}</p>
        ) : data ? (
          <div className="rounded-2xl bg-white p-3 shadow-2xl">
            <canvas ref={canvas} aria-label={t('Attendance QR code')} />
          </div>
        ) : (
          <p className="text-slate-400">{t('Connecting…')}</p>
        )}
        <p className="text-center text-lg text-slate-300">{t('Scan with your phone camera to clock in or out')}</p>
      </main>
      <footer className="flex items-center gap-2 text-xs text-slate-500">
        {offline && (
          <span className="inline-flex items-center gap-1 text-amber-400">
            <WifiOff className="h-3.5 w-3.5" /> {t('Reconnecting…')}
          </span>
        )}
        <span>{data?.deviceName}</span>
        <span>· {t('code changes every 30 seconds')}</span>
      </footer>
    </div>
  );
}
